/**
 * Pure helpers for the WagePro-branded login screen (WagePro signup epic,
 * `wagepro_login_screen.tsx`). No React, no I/O — unit-tested in isolation,
 * matching this codebase's existing `auth_gate_screen.ts`/
 * `wagepro_signup_logic.ts` "pure logic" convention.
 *
 * Calls `wageproAuth.login` (`apps/api/src/trpc/routers/wagepro_auth.ts`),
 * which reuses `auth.login`'s own core verification pipeline
 * (`login_core.ts`). This module does not reimplement any server-side
 * validation (password length, lockout, Turnstile) — only the lightest
 * client-side "did you type something" pre-check, since this is a login
 * form, not a signup form.
 */

/** The pre-auth pathname `main.tsx` gates on, mirroring `SIGNUP_PATH` (`wagepro_signup_logic.ts`). */
export const LOGIN_PATH = '/login';

export interface WageProLoginFormValues {
  email: string;
  password: string;
}

/**
 * Which fields currently fail client-side validation. A key present (any
 * value, always `true`) means that field is invalid; the screen maps each
 * key to its own translated error copy — this module never renders text.
 */
export type LoginFieldErrors = Partial<
  Record<keyof WageProLoginFormValues, true>
>;

/**
 * Deliberately shallow: "did the user type SOMETHING" — never password
 * length or email-shape rules. Those are signup-time concerns
 * (`wagepro_signup_logic.ts`'s `validateSignupForm`); re-checking them here
 * would reject a real existing account whose password predates a since
 * -tightened rule, or whose email address the server already accepted at
 * registration time. The server's own credential check is the actual
 * source of truth for "is this a valid login" either way.
 */
export function validateLoginForm(
  values: WageProLoginFormValues,
): LoginFieldErrors {
  const errors: LoginFieldErrors = {};
  if (values.email.trim().length === 0) errors.email = true;
  if (values.password.length === 0) errors.password = true;
  return errors;
}

export function hasLoginFieldErrors(errors: LoginFieldErrors): boolean {
  return Object.keys(errors).length > 0;
}

// A caught `wageproAuth.login` rejection is turned into display copy by
// `classifyAuthError` (`lib/auth/auth_error_display.ts`), shared with the
// signup screen — see that module for why `err.message` is no longer shown
// unconditionally.

/**
 * Query param that marks a `/login` navigation as originating from a
 * DELIBERATE sign-out (`AuthStatus === 'signed_out'`), not a plain cold
 * boot (`unauthenticated`) — `main.tsx`'s `AuthGate` boot-redirect effect
 * sets this when it sends the browser here instead of calling
 * `beginSignIn()` directly (see that effect's comment, and
 * `auth_gate_screen.ts`'s `resolveAutoSignInPrompt` for the full history:
 * PR #22341's `REAUTHENTICATE_PROMPT`/`prompt=login` reasoning).
 *
 * Read by THIS screen only to show a "you were signed out" message — it is
 * deliberately NOT threaded into this screen's own later `beginSignIn()`
 * call after a successful `auth.login`. That is a considered decision, not
 * an oversight: the risk `REAUTHENTICATE_PROMPT` originally guarded against
 * was `GET /oauth/authorize` silently answering from a still-live Ledger
 * session cookie the user never proved they still control — bypassing
 * sign-out entirely. Landing on THIS screen removes that risk structurally:
 * there is no path from here into the app that skips the email/password
 * form, and a successful `auth.login` call OVERWRITES the session cookie
 * with a freshly-issued one for the account just verified
 * (`login_core.ts`'s session-fixation defense — it destroys any pre-login
 * session before minting the new one). By the time this screen calls
 * `beginSignIn()`, the cookie is first-hand proof of credentials entered
 * seconds ago, not a stale leftover — forcing `prompt=login` on top of that
 * would only make the user type their password a SECOND time at Ledger's
 * own sign-in page, breaking the "carries straight through with no second
 * login prompt" property `wagepro_signup_screen.tsx` documents and this
 * codebase has already hand-verified end to end.
 */
export const FORCE_REAUTH_PARAM = 'reauth';

/** The single valid value {@link FORCE_REAUTH_PARAM} carries — kept as a named constant, never inlined. */
export const FORCE_REAUTH_VALUE = '1';

/**
 * Builds the URL `main.tsx`'s boot-redirect effect navigates to —
 * {@link LOGIN_PATH}, optionally carrying {@link FORCE_REAUTH_PARAM} so the
 * login screen knows it was reached via a deliberate sign-out.
 */
export function buildLoginPath(
  options: { forceReauth?: boolean } = {},
): string {
  return options.forceReauth
    ? `${LOGIN_PATH}?${FORCE_REAUTH_PARAM}=${FORCE_REAUTH_VALUE}`
    : LOGIN_PATH;
}

/** Reads {@link FORCE_REAUTH_PARAM} from a query string (`window.location.search`). */
export function wasForceReauthRequested(search: string): boolean {
  return (
    new URLSearchParams(search).get(FORCE_REAUTH_PARAM) === FORCE_REAUTH_VALUE
  );
}

/**
 * Query param that marks a `/login` navigation as originating from Ledger's
 * Apps launcher (`apps/ledger-web/src/components/layout/apps_launcher.tsx`) —
 * the caller is asking WagePro to start the "Sign in with Ledger" PKCE
 * handoff immediately instead of rendering the email/password form. See
 * issue #23943 for the launcher UX motivation (Xero-style consent-first
 * flow).
 *
 * SECURITY: this auto-handoff MUST NOT fire when {@link FORCE_REAUTH_PARAM}
 * is also present. A deliberate sign-out (`AuthStatus === 'signed_out'`)
 * lands here via `buildLoginPath({ forceReauth: true })` — that path exists
 * precisely to keep a still-live Ledger session cookie from silently
 * bypassing the sign-out, so a launcher param on top of it must not be
 * allowed to reintroduce the very bypass sign-out is defending against. PR
 * #22341 introduced the sign-out defense; this handoff preserves it by
 * gating on `!wasForceReauthRequested(search)` at the call site (see
 * `wagepro_login_screen.tsx`'s auto-SSO effect).
 */
export const LEDGER_SSO_PARAM = 'sso';

/** The single valid value {@link LEDGER_SSO_PARAM} carries — kept as a named constant, never inlined. */
export const LEDGER_SSO_VALUE = 'ledger';

/** Query param carrying the launching organisation's entity id (a hint — not trusted locally). */
export const LEDGER_SSO_ENTITY_HINT_PARAM = 'entity_id';

const ENTITY_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * Reads {@link LEDGER_SSO_ENTITY_HINT_PARAM} from a query string, returning
 * the value only when it matches the safe-identifier pattern. Returns `null`
 * for a missing, empty, or malformed value — the hint is optional and WagePro
 * never trusts it locally.
 */
export function readLedgerSsoEntityHint(search: string): string | null {
  const value = new URLSearchParams(search).get(LEDGER_SSO_ENTITY_HINT_PARAM);
  if (!value || !ENTITY_ID_RE.test(value)) return null;
  return value;
}

/**
 * Builds the URL Ledger's Apps launcher navigates to when the user clicks
 * WagePro (`apps/ledger-web/src/components/layout/apps_launcher.tsx`).
 * Composes with {@link buildLoginPath}'s existing `forceReauth` option — the
 * launcher never sets both, but the auto-handoff on the receiving end still
 * checks each independently and refuses to fire when both are present (see
 * {@link LEDGER_SSO_PARAM}'s security note).
 *
 * The optional `entityId` appends {@link LEDGER_SSO_ENTITY_HINT_PARAM} so
 * Ledger can pre-select the right organisation. Callers without an entity
 * context (e.g. `needs_ledger_sso` redirects in `main.tsx`) omit it and the
 * URL is unchanged from before.
 */
export function buildLedgerSsoLoginPath(
  options: { entityId?: string } = {},
): string {
  const base = `${LOGIN_PATH}?${LEDGER_SSO_PARAM}=${LEDGER_SSO_VALUE}`;
  if (options.entityId && ENTITY_ID_RE.test(options.entityId)) {
    return `${base}&${LEDGER_SSO_ENTITY_HINT_PARAM}=${options.entityId}`;
  }
  return base;
}

/**
 * Reads {@link LEDGER_SSO_PARAM} from a query string. Returns `true` ONLY
 * when the param carries {@link LEDGER_SSO_VALUE} AND
 * {@link FORCE_REAUTH_PARAM} is absent — a deliberate sign-out never
 * auto-hands off (see {@link LEDGER_SSO_PARAM}'s security note). Callers
 * therefore never need to combine two checks themselves.
 */
export function wasLedgerSsoHandoffRequested(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.get(LEDGER_SSO_PARAM) === LEDGER_SSO_VALUE &&
    params.get(FORCE_REAUTH_PARAM) !== FORCE_REAUTH_VALUE
  );
}
